import React from 'react'
import { useWeather } from '../hooks/useWeather'

interface WeatherWidgetProps {
  zipCode: string | undefined
}

const WeatherWidget: React.FC<WeatherWidgetProps> = ({ zipCode }) => {
  const { weather, alerts, loading, error } = useWeather(zipCode)

  if (loading) {
    return (
      <div className="card">
        <div className="spinner" style={{ width: '30px', height: '30px' }} />
      </div>
    )
  }

  if (error || !weather) {
    return (
      <div className="card">
        <p style={{ color: '#666' }}>Weather data unavailable</p>
      </div>
    )
  }

  const getWeatherIcon = (condition: string) => {
    switch (condition.toLowerCase()) {
      case 'sunny': return '☀️'
      case 'partly cloudy': return '⛅'
      case 'cloudy': return '☁️'
      case 'rain': return '🌧️'
      default: return '🌤️'
    }
  }

  return (
    <div>
      {/* Alerts */}
      {alerts.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {alerts.map((alert, index) => (
            <div 
              key={index}
              className={`alert alert-${alert.severity === 'high' ? 'error' : alert.severity === 'medium' ? 'warning' : 'success'}`}
              style={{ marginBottom: '0.5rem' }}
            >
              <strong>
                {alert.type === 'frost' && '❄️ '}
                {alert.type === 'heat' && '🔥 '}
                {alert.type === 'rain' && '🌧️ '}
                {alert.type === 'wind' && '💨 '}
              </strong>
              {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Current Weather */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: 0 }}>Current Weather</h3>
            <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
              {weather.condition} • {weather.humidity}% humidity
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '3rem' }}>{getWeatherIcon(weather.condition)}</span>
            <p style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>{weather.temp}°F</p>
          </div>
        </div>
      </div>

      {/* 7-Day Forecast */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <h3>7-Day Forecast</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '0.5rem', marginTop: '1rem' }}>
          {weather.forecast.map((day, index) => (
            <div 
              key={index}
              style={{ 
                textAlign: 'center', 
                padding: '0.75rem 0.5rem',
                background: index === 0 ? '#e8f5e9' : 'white',
                borderRadius: '8px',
                border: '1px solid #ddd'
              }}
            >
              <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 500 }}>
                {index === 0 ? 'Today' : day.date.toLocaleDateString('en-US', { weekday: 'short' })}
              </p>
              <span style={{ fontSize: '1.5rem', display: 'block', margin: '0.5rem 0' }}>
                {getWeatherIcon(day.condition)}
              </span>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                <strong>{day.high}°</strong> / {day.low}°
              </p>
              {day.precipitation > 20 && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#2196f3' }}>
                  💧 {day.precipitation}%
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Gardening Tips Based on Weather */}
      <div className="card" style={{ marginTop: '1rem' }}>
        <h3>💡 Weather Tips</h3>
        <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', color: '#555' }}>
          {weather.precipitation && weather.precipitation > 30 && (
            <li>Rain expected - skip watering today</li>
          )}
          {weather.temp > 85 && (
            <li>High heat - water deeply in early morning</li>
          )}
          {weather.temp < 40 && (
            <li>Cold weather - protect tender plants</li>
          )}
          {weather.humidity > 80 && (
            <li>High humidity - watch for fungal diseases</li>
          )}
          {(!weather.precipitation || weather.precipitation <= 30) && weather.temp <= 85 && weather.temp >= 40 && (
            <li>Good conditions for planting and transplanting</li>
          )}
        </ul>
      </div>
    </div>
  )
}

export default WeatherWidget
